import { useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Loader2, MapPin, Search, X } from 'lucide-react';
import { cn } from './utils';
import { discoverAddress, type NormalizedAddress } from './here-maps';
import './address-autocomplete.css';

/**
 * AddressAutocomplete — modal-based address search with manual fallback.
 *
 * Visual contract (matches the reference screenshots — see PR
 * description):
 *
 *   - The TRIGGER is an inline cell that visually fits the rest of
 *     the form (same height/radius/bg as a FloatingLabelInput at
 *     rest, with a leading magnifying glass icon).
 *   - On click, it opens a centered MODAL DIALOG with a search
 *     field, an "Annulla" text-link on the right, and the result
 *     list below. The modal sits on a dimmed/blurred backdrop —
 *     the form fields behind stay visible but de-emphasised.
 *   - The first row in the result list is ALWAYS
 *     "Il mio indirizzo non è presente" — the explicit manual
 *     fallback CTA. Selecting it just closes the modal; the user
 *     types into the form fields below directly. This is the
 *     visible-affordance side of the manual-fallback rule that's
 *     mandatory for this primitive (project memory).
 *   - When the search is empty (or shorter than 3 chars), the
 *     result area shows an empty hint ("I risultati della ricerca
 *     saranno visualizzati qui") instead of the list.
 *
 * UX details:
 *   - 300ms debounce on the input (matches HERE's free-tier 5 TPS
 *     limit headroom; doesn't burn session-token on every keystroke).
 *   - AbortController cancels in-flight requests on new query so
 *     results can't arrive out of order.
 *   - Min 3 chars before firing a query (single/double letters
 *     return noise on HERE).
 *   - Selecting a real result calls `onSelect` and closes the
 *     modal; the parent populates the form fields below.
 *   - Graceful degradation: when `VITE_HERE_API_KEY` is unset, the
 *     primitive renders nothing (returns null) and the form stays
 *     fully usable in manual mode.
 *
 * Why raw Radix Dialog (not our `DialogContent` wrapper):
 *   The shared `DialogContent` hardcodes a top-right `X` close
 *   button that conflicts with our "Annulla" text-link CTA. Using
 *   `@radix-ui/react-dialog` primitives directly gives us full
 *   control over the close affordance without touching the shared
 *   primitive (governance-friendly).
 */

export interface AddressAutocompleteProps {
  /** Trigger label — visible inside the inline cell (e.g. "Cerca indirizzo"). */
  label: string;
  /**
   * HERE Maps API key. When falsy the primitive renders nothing — the
   * form stays usable in manual mode. Each app passes its own
   * env-derived value (e.g. `env.VITE_HERE_API_KEY`).
   */
  apiKey: string | undefined;
  /** Called with the normalized address when the user picks a real suggestion. */
  onSelect: (addr: NormalizedAddress) => void;
  /** Optional className passed to the trigger wrapper. */
  className?: string;
  /** Disable the trigger (e.g. while parent form is saving). */
  disabled?: boolean;
}

export function AddressAutocomplete({
  label,
  apiKey,
  onSelect,
  className,
  disabled,
}: AddressAutocompleteProps): JSX.Element | null {
  const [open, setOpen] = useState(false);

  if (!apiKey) return null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-control w-full items-center gap-2 rounded-md bg-foreground/5 px-3 text-left text-sm text-muted-foreground transition-colors hover:bg-foreground/10 focus-visible:bg-foreground/10 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <Search className="h-4 w-4 flex-none" aria-hidden />
          <span>{label}</span>
        </button>
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        {/* Scrim theme-agnostic — vedi address-autocomplete.css per il
         * background-color. data-address-modal-overlay applica
         * rgba(0,0,0,0.6) che scurisce in entrambi i temi (foreground/X
         * di Tailwind invertirebbe in dark mode). */}
        <DialogPrimitive.Overlay
          data-address-modal-overlay
          className="fixed inset-0 z-overlay backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        {/* Height contract: h-[32rem] (mirror of CountryPicker /
            PhoneCountryPicker) so the popup doesn't jump as HERE Maps
            results stream in. The `max-h-[80vh]` cap is the only
            shrink path, and it kicks in solo su viewport molto bassi. */}
        <DialogPrimitive.Content
          data-address-modal
          className="fixed left-1/2 top-1/2 z-modal flex h-[32rem] max-h-[80vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col gap-3 rounded-2xl border border-border/10 p-4 text-popover-foreground shadow-xl"
        >
          <DialogPrimitive.Title className="sr-only">
            Cerca indirizzo
          </DialogPrimitive.Title>
          <SearchModalBody
            apiKey={apiKey}
            onSelect={(addr) => {
              onSelect(addr);
              setOpen(false);
            }}
            onDismiss={() => setOpen(false)}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * Inner body of the search dialog. Lives in its own component so the
 * search-state lifetime (query, results, loading) is reset every time
 * the dialog opens — Radix unmounts/remounts Content on close by
 * default, which gives us the reset for free.
 */
function SearchModalBody({
  apiKey,
  onSelect,
  onDismiss,
}: {
  apiKey: string;
  onSelect: (addr: NormalizedAddress) => void;
  onDismiss: () => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<NormalizedAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Autofocus the input on mount — first paint is when the dialog
    // opens, so this puts the cursor in the search field immediately.
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 3) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => {
      discoverAddress(trimmed, apiKey, ctrl.signal)
        .then((items) => {
          setResults(items);
          setError(null);
        })
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setResults([]);
          setError('Ricerca non disponibile');
        })
        .finally(() => {
          setLoading(false);
        });
    }, 300);
    return () => {
      window.clearTimeout(timer);
      ctrl.abort();
    };
  }, [query, apiKey]);

  const showResultsArea = query.trim().length >= 3;

  return (
    <>
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            ref={inputRef}
            type="text"
            placeholder="Cerca indirizzo"
            autoComplete="off"
            spellCheck={false}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-10 w-full rounded-md bg-foreground/5 pl-9 pr-9 text-sm text-foreground outline-none transition-colors hover:bg-foreground/10 focus-visible:bg-foreground/10"
          />
          {query.length > 0 ? (
            <button
              type="button"
              aria-label="Cancella"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-sm font-medium text-primary transition-opacity hover:opacity-80"
        >
          Annulla
        </button>
      </div>

      {/* The outer Dialog Content is now fixed at h-[32rem] — the body
       * just fills the remaining space (flex-1) and scrolls when the
       * results overflow. The previous `min-h-72` hack is no longer
       * needed: with the fixed-height contract on the wrapper there is
       * no "shrink to fit" mode to defend against. */}
      <div className="flex flex-1 flex-col overflow-auto">
        {!showResultsArea ? (
          <EmptyHint />
        ) : (
          <ul className="flex flex-col">
            <li>
              <button
                type="button"
                onClick={onDismiss}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-left text-sm text-primary transition-colors hover:bg-foreground/10 focus:bg-foreground/10 focus:outline-none"
              >
                Il mio indirizzo non è presente
              </button>
            </li>
            {loading ? (
              <li className="flex items-center gap-2 px-3 py-2.5 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Ricerca…
              </li>
            ) : error !== null ? (
              <li className="px-3 py-2.5 text-sm text-state-danger">{error}</li>
            ) : results.length === 0 ? (
              <li className="px-3 py-2.5 text-sm text-muted-foreground">
                Nessun risultato.
              </li>
            ) : (
              results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(r)}
                    className="flex w-full items-start gap-2 rounded-md px-3 py-2.5 text-left text-sm transition-colors hover:bg-foreground/10 focus:bg-foreground/10 focus:outline-none"
                  >
                    <MapPin
                      className="mt-0.5 h-3.5 w-3.5 flex-none text-muted-foreground"
                      aria-hidden
                    />
                    <span className="flex-1 font-medium">{r.label}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </>
  );
}

function EmptyHint(): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center text-sm text-muted-foreground">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-foreground/5">
        <Search className="h-4 w-4" aria-hidden />
      </div>
      <p>I risultati della ricerca saranno visualizzati qui</p>
    </div>
  );
}
